import React, { ReactNode } from 'react';
import { useDensity } from '../../../hooks/useDensity';

export interface ModalFooterProps {
  children?: ReactNode;
  className?: string;
  justify?: 'start' | 'center' | 'end' | 'between' | 'around' | 'evenly';
  wrap?: boolean;
  sticky?: boolean;
  divider?: boolean;
  variant?: 'default' | 'actions' | 'navigation';

  // Quick action props
  primaryAction?: {
    label: string;
    onClick: () => void;
    loading?: boolean;
    disabled?: boolean;
    variant?: 'primary' | 'secondary' | 'danger';
  };

  secondaryAction?: {
    label: string;
    onClick: () => void;
    disabled?: boolean;
  };

  // Navigation props
  onBack?: () => void;
  onNext?: () => void;
  backLabel?: string;
  nextLabel?: string;
  nextDisabled?: boolean;
}

const ModalFooter: React.FC<ModalFooterProps> = ({
  children,
  className = '',
  justify = 'end',
  wrap = true,
  sticky = false,
  divider = true,
  variant = 'default',
  primaryAction,
  secondaryAction,
  onBack,
  onNext,
  backLabel = 'Back',
  nextLabel = 'Next',
  nextDisabled = false,
}) => {
  const { classes, isCompact, isNormal } = useDensity();

  // Base classes
  const baseClasses = classes(
    'bg-gray-50',
    isCompact ? 'px-4 py-3' : isNormal ? 'px-6 py-4' : 'px-8 py-6'
  );

  // Justify content classes
  const justifyClasses = {
    start: 'justify-start',
    center: 'justify-center',
    end: 'justify-end',
    between: 'justify-between',
    around: 'justify-around',
    evenly: 'justify-evenly',
  }[justify];

  // Gap classes based on density
  const gapClasses = isCompact ? 'gap-2' : isNormal ? 'gap-3' : 'gap-4';

  const footerClasses = `
    ${baseClasses}
    ${divider ? 'border-t border-gray-200' : ''}
    ${sticky ? 'sticky bottom-0' : ''}
    flex items-center ${justifyClasses}
    ${wrap ? 'flex-wrap' : ''}
    ${gapClasses}
    ${className}
  `
    .trim()
    .replace(/\s+/g, ' ');

  // Button size classes based on density
  const buttonSizeClasses = isCompact
    ? 'px-3 py-1.5 text-sm'
    : isNormal
      ? 'px-4 py-2 text-sm'
      : 'px-6 py-3 text-base';

  // Button variants
  const buttonVariants = {
    primary: `${buttonSizeClasses} bg-blue-600 text-white border border-blue-600 rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed`,
    secondary: `${buttonSizeClasses} bg-white text-gray-700 border border-gray-300 rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed`,
    danger: `${buttonSizeClasses} bg-red-600 text-white border border-red-600 rounded-md hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-red-500 disabled:opacity-50 disabled:cursor-not-allowed`,
  };

  // Render button with loading state
  const renderButton = (
    label: string,
    onClick: () => void,
    variant: 'primary' | 'secondary' | 'danger' = 'primary',
    disabled = false,
    loading = false
  ) => (
    <button onClick={onClick} disabled={disabled || loading} className={buttonVariants[variant]}>
      {loading && (
        <div
          className={`inline-block animate-spin rounded-full border-2 border-white border-t-transparent mr-2 ${isCompact ? 'h-3 w-3' : 'h-4 w-4'}`}
        />
      )}
      {label}
    </button>
  );

  // Render based on variant
  const renderContent = () => {
    if (children) {
      return children;
    }

    switch (variant) {
      case 'actions':
        return (
          <>
            {secondaryAction &&
              renderButton(
                secondaryAction.label,
                secondaryAction.onClick,
                'secondary',
                secondaryAction.disabled
              )}
            {primaryAction &&
              renderButton(
                primaryAction.label,
                primaryAction.onClick,
                primaryAction.variant || 'primary',
                primaryAction.disabled,
                primaryAction.loading
              )}
          </>
        );

      case 'navigation':
        return (
          <div className="flex justify-between w-full">
            <div>{onBack && renderButton(backLabel, onBack, 'secondary')}</div>
            <div>{onNext && renderButton(nextLabel, onNext, 'primary', nextDisabled)}</div>
          </div>
        );

      default:
        return null;
    }
  };

  const content = renderContent();

  // Don't render footer if no content
  if (!content) {
    return null;
  }

  return <div className={footerClasses}>{content}</div>;
};

// Specialized footer components for common patterns

export const ModalActionFooter: React.FC<
  Pick<ModalFooterProps, 'primaryAction' | 'secondaryAction' | 'className' | 'sticky' | 'divider'>
> = (props) => {
  return <ModalFooter variant="actions" justify="end" {...props} />;
};

export const ModalNavigationFooter: React.FC<
  Pick<
    ModalFooterProps,
    | 'onBack'
    | 'onNext'
    | 'backLabel'
    | 'nextLabel'
    | 'nextDisabled'
    | 'className'
    | 'sticky'
    | 'divider'
  >
> = (props) => {
  return <ModalFooter variant="navigation" {...props} />;
};

export const ModalFormFooter: React.FC<{
  onCancel: () => void;
  onSubmit: () => void;
  submitLabel?: string;
  cancelLabel?: string;
  submitDisabled?: boolean;
  submitLoading?: boolean;
  className?: string;
}> = ({
  onCancel,
  onSubmit,
  submitLabel = 'Save',
  cancelLabel = 'Cancel',
  submitDisabled = false,
  submitLoading = false,
  className,
}) => {
  return (
    <ModalActionFooter
      className={className}
      secondaryAction={{
        label: cancelLabel,
        onClick: onCancel,
      }}
      primaryAction={{
        label: submitLabel,
        onClick: onSubmit,
        disabled: submitDisabled,
        loading: submitLoading,
      }}
    />
  );
};

export default ModalFooter;
